<template>
  <template v-if="mode === 'edit'">
    <div
      :class="[
        'pt-2 pb-4 date-field w-full',
        warning ? 'warning' : '',
        field.error ? 'my-2 px-2 rounded border-2 border-brand-red bg-brand-red bg-opacity-10' : ''
      ]"
      :name="field.name"
      :field-id="field.field_id"
      :required="field.required"
    >
      <label class="my-2 block">
        {{ field.label }}<span v-if="field.required" class="text-brand-red ml-1">*</span>
      </label>
      <div class="flex flex-col md:flex-row">
        <div class="flex flex-row pb-2">
          <div class="w-1/3 mr-3">
            <DropDown
              :options="months"
              placeholder="Month"
              :initial="initialStartMonth"
              @select="updateMonth('start', $event)"
              class="month"
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1 mr-3">
            <DropDown
              :options="days"
              placeholder="Day"
              :initial="initialStartDay"
              @select="updateDay('start', $event)"
              class="day"
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1">
            <input
              type="text"
              class="w-full px-4 py-2 border-1 border-brand-mid-gray shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green text-sm md:text-base"
              placeholder="Year"
              :class="{ 'bg-brand-light-gray': !!start.year }"
              v-model="start.year"
              v-maska="'####'"
              @change="updateValue"
            />
          </div>
        </div>

        <div class="flex items-center">
          <MinusIcon class="w-full md:w-6 mx-5 h-6 text-brand-dark-gray" />
        </div>

        <div class="flex flex-row pb-2">
          <div class="w-1/3 mr-3">
            <DropDown
              :options="months"
              placeholder="Month"
              :initial="initialEndMonth"
              @select="updateMonth('end', $event)"
              class="month"
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1 mr-3">
            <DropDown
              :options="days"
              placeholder="Day"
              :initial="initialEndDay"
              @select="updateDay('end', $event)"
              class="day"
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1">
            <input
              type="text"
              class="w-full px-4 py-2 border-1 border-brand-mid-gray shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green text-sm md:text-base"
              placeholder="Year"
              :class="{ 'bg-brand-light-gray': !!end.year }"
              v-model="end.year"
              v-maska="'####'"
              @change="updateValue"
            />
          </div>
        </div>
      </div>
      <template v-if="field.help_text">
        <p class="text-brand-mid-gray italic text-xs">{{ field.help_text }}</p>
      </template>
      <template v-if="field.error">
        <p class="text-brand-red py-1">{{ errorMessage }}</p>
      </template>
    </div>
  </template>
  <template v-if="mode === 'view'">
    <div class="flex items-center md:w-1/2 py-1 px-1">
      <p>{{ field.label }}</p>
    </div>
    <div class="flex items-center md:w-1/2 py-1 px-1">
      <p class="font-semibold">
        {{ displayDate }}
      </p>
    </div>
  </template>
  <template v-if="mode === 'form_edit'">
    <div class="flex flex-col w-full">
      <div class="flex flex-col md:flex-row">
        <div class="flex flex-row w-full py-2">
          <div class="w-1/2 md:flex-1 mr-3">
            <DropDown
              placeholder="Month"
              :initial="{ value: '01', label: 'Month' }"
              class="month"
              :options="[]"
              disabled
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1 mr-3">
            <DropDown
              placeholder="Day"
              :initial="{ value: '01', label: 'Day' }"
              class="day"
              :options="[]"
              disabled
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1">
            <input
              type="text"
              class="w-full px-4 py-2 border-1 border-brand-mid-gray shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green bg-brand-light-gray text-sm md:text-base"
              disabled
              value="Year"
              placeholder="Year"
            />
          </div>
        </div>
        <div class="flex items-center">
          <MinusIcon class="w-full md:w-6 mx-5 w-6 h-6 text-brand-dark-gray" />
        </div>
        <div class="flex flex-row w-full py-2">
          <div class="w-1/2 md:flex-1 mr-3">
            <DropDown
              placeholder="Month"
              :initial="{ value: '01', label: 'Month' }"
              class="month"
              :options="[]"
              disabled
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1 mr-3">
            <DropDown
              placeholder="Day"
              :initial="{ value: '01', label: 'Day' }"
              class="day"
              :options="[]"
              disabled
            ></DropDown>
          </div>
          <div class="w-1/4 md:flex-1">
            <input
              type="text"
              class="w-full px-4 py-2 border-1 border-brand-mid-gray shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green bg-brand-light-gray text-sm md:text-base"
              disabled
              value="Year"
              placeholder="Year"
            />
          </div>
        </div>
      </div>
      <TextDisplayEditToggle
        :helpText="field.help_text"
        :type="'field_subtext'"
        @input="updateHelpText"
        :disabled="field.editable === false"
      />
    </div>
  </template>
</template>
<script>
import moment from "moment"
import { months, days } from "@/constants/dateOptions"

import { MinusIcon } from "@heroicons/vue/outline"

export default {
  components: {
    MinusIcon
  },
  props: {
    field: Object,
    mode: {
      type: String
    },
    stage: {
      type: String
    }
  },
  emits: ["update"],
  data() {
    return {
      months,
      days,
      start: {},
      end: {},
      initialStartMonth: null,
      initialStartDay: null,
      initialEndMonth: null,
      initialEndDay: null,
      warning: false,
      errorMessage: ""
    }
  },
  mounted() {
    // If editing field value, map the dropdowns and year input
    //this.field.value = 01-01-1990:01-01-1990
    if (this.field.value) {
      const start = this.field.value.split(":")[0]
      const end = this.field.value.split(":")[1]

      this.start.year = start.split("-")[0]
      this.start.month = start.split("-")[1]
      this.start.day = start.split("-")[2]

      this.end.year = end.split("-")[0]
      this.end.month = end.split("-")[1]
      this.end.day = end.split("-")[2]

      this.initialStartMonth = this.months.find((el) => el.value === this.start.month)
      this.initialStartDay = this.days.find((el) => el.value === this.start.day)

      this.initialEndMonth = this.months.find((el) => el.value === this.end.month)
      this.initialEndDay = this.days.find((el) => el.value === this.end.day)
    }
  },
  computed: {
    formattedStartDate() {
      return `${this.start.year}-${this.start.month}-${this.start.day}`
    },
    formattedEndDate() {
      return `${this.end.year}-${this.end.month}-${this.end.day}`
    },
    validDate() {
      return (
        moment(this.formattedStartDate, "YYYY-MM-DD", true).isValid() &&
        moment(this.formattedEndDate, "YYYY-MM-DD", true).isValid()
      )
    },
    isValid() {
      if (!this.field.value && this.field.required) {
        return false
      }
      if (this.field.value && !this.validDate) {
        return false
      }
      return true
    },
    displayDate() {
      if (!this.field || !this.field.value) {
        return "Not entered"
      }

      if (!this.isValid) {
        return "Invalid date entered"
      }

      return `${moment(this.field.value.split(":")[0]).format("MM-DD-YYYY")} ~ ${moment(
        this.field.value.split(":")[1]
      ).format("MM-DD-YYYY")}`
    }
  },
  methods: {
    closeWarning() {
      this.warning = false
    },
    updateHelpText(ev) {
      // eslint-disable-next-line vue/no-mutating-props
      this.field.help_text = ev.target.value
    },
    updateValue() {
      if (this.validDate) {
        this.$emit("update", `${this.formattedStartDate}:${this.formattedEndDate}`)
        this.validateValue()
      }
    },
    updateMonth(type, val) {
      this[type].month = val.value
      this.updateValue()
    },
    updateDay(type, val) {
      this[type].day = val.value
      this.updateValue()
    },
    validateValue() {
      //TODO: make sure range start < end
    },
    validateField() {
      if (!this.isValid) {
        if (this.field.required && !this.field.value) {
          this.errorMessage = "This field is required."
        }
      } else {
        this.errorMessage = ""
      }
    }
  }
  // watch: {
  //   year() {
  //     this.updateValue()
  //   }
  // }
}
</script>
<style lang="scss" scoped>
.warning {
  background: #fefae8;
  @apply px-2;
}
</style>
